import React, { useEffect, useRef, useCallback } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeIntroPopup,
  toggleBackgroundMusic,
  setIntroTransitionActive,
  setShowGameAfterTransition,
} from "../../../State/actions";
import {
  selectIntroTransitionActive,
  selectHoldAndWinActive,
} from "../../../State/selectors";
import { useAppActivity } from "../../../Contexts/AppActivityContext";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import anime from "animejs";
import * as PIXI from "pixi.js";

const IntroTransitionOverlay = () => {
  const dispatch = useDispatch();
  const { startIdleTimer } = useAppActivity();
  const { dimensions } = useDimensions();
  const { backgroundAnimationControllerRef } = useReelRefs();
  const overlayRef = useRef(null);
  const animationRef = useRef(null);
  const introTransitionActive = useSelector(selectIntroTransitionActive);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  // Get sound state to respect user's preferences
  const { backgroundMusicEnabled, globalSoundDisabled } = useSelector(
    (state) => state.sound
  );

  // Create black overlay graphics
  const createBlackOverlay = useCallback(() => {
    const graphics = new PIXI.Graphics();
    graphics.beginFill(0x000000);
    graphics.drawRect(0, 0, dimensions.width, dimensions.height);
    graphics.endFill();
    return graphics;
  }, [dimensions.width, dimensions.height]);

  // Handle transition animation
  useEffect(() => {
    if (introTransitionActive && overlayRef.current) {
      // Clear any existing animation
      if (animationRef.current) {
        animationRef.current.pause();
      }

      // Remove old overlay and create new one
      overlayRef.current.removeChildren();
      overlayRef.current.addChild(createBlackOverlay());

      // Create cinematic transition: fade in black → pause → fade out to reveal game
      const animation = anime.timeline({
        complete: () => {
          dispatch(setIntroTransitionActive(false));
          startIdleTimer();
        },
      });

      // Phase 1: Fade in black overlay to cover the intro screen
      animation.add({
        targets: overlayRef.current,
        alpha: [0, 1],
        duration: 400,
        easing: "easeInQuad",
        complete: () => {
          // Screen is now entirely black - clear background animation and remove intro
          const bgController = backgroundAnimationControllerRef.current;
          if (bgController?.backgroundSpine) {
            // Clear all animation tracks
            if (bgController.backgroundSpine.state) {
              bgController.backgroundSpine.state.clearTracks();
            }
            // Reset skeleton to setup pose to remove all visual elements
            if (bgController.backgroundSpine.skeleton?.setToSetupPose) {
              bgController.backgroundSpine.skeleton.setToSetupPose();
            }
            // Play appropriate idle animation
            if (holdAndWinActive) {
              bgController.playFeatureIdle?.();
            } else {
              bgController.playBaseIdle?.();
            }
          }

          // Remove intro and show game underneath
          dispatch(closeIntroPopup());
          dispatch(setShowGameAfterTransition(true));
        },
      });

      // Phase 2: Brief dramatic pause with full black coverage
      animation.add({
        targets: overlayRef.current,
        alpha: 1,
        duration: 200,
      });

      // Phase 3: Fade out black overlay to reveal the game
      animation.add({
        targets: overlayRef.current,
        alpha: [1, 0],
        duration: 500,
        easing: "easeOutQuad",
        complete: () => {
          // Game is now fully visible - start background music only if:
          // 1. Global sound is not disabled
          // 2. Background music is currently disabled (so we enable it)
          // This respects the user's choice if they disabled music in the intro screen
          if (!globalSoundDisabled && !backgroundMusicEnabled) {
            dispatch(toggleBackgroundMusic());
          }
        },
      });

      animationRef.current = animation;

      return () => {
        if (animationRef.current) {
          animationRef.current.pause();
          animationRef.current = null;
        }
      };
    }
  }, [
    introTransitionActive,
    createBlackOverlay,
    dispatch,
    startIdleTimer,
    globalSoundDisabled,
    backgroundMusicEnabled,
    backgroundAnimationControllerRef,
    holdAndWinActive,
  ]);

  // Only render when there's an active transition
  if (!introTransitionActive) return null;

  return (
    <Container
      name="intro_transition_overlay"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={10000} // Above absolutely everything
    />
  );
};

export default IntroTransitionOverlay;
